import { ApiError } from '@iheartjane/data-access';
import { useCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useEffect } from 'react';

import {
  updateLocalCartUuid,
  useGetCart,
  useLocalCartUuid,
} from '@jane/kiosk/data-access';
import type { EmptyCart } from '@jane/shared/types';

const EMPTY_CART: EmptyCart = {};

/**
 * The single kiosk entry point for reading the active cart.
 *
 * 1. The `c3UseV3Cart` flag branch between the v3 `useCart` and the v1
 *    `useGetCart`.
 * 2. The reactive local cart uuid (see {@link useLocalCartUuid}), so creating
 *    the first cart flips `useCart`'s `enabled` gate and the UI updates.
 * 3. The cart-lifecycle safety net that v1 centralized inside `useGetCart`
 *    (`getCart.ts`) and the migration scattered away: when the active cart no
 *    longer exists, drop the local uuid so every consumer re-renders to an empty
 *    bag. Removing the last product destroys the cart server-side
 *    (`\@cart.destroy! if \@cart.unfilled?`), after which the invalidate-driven
 *    refetch 404s. Without this, React Query keeps the last good cart and the
 *    bag keeps rendering a product that's gone. Clearing the uuid is enough:
 *    consumers re-key `useCart` to the empty/disabled state, the stale
 *    `cartKeys.detail(uuid)` entry is left to React Query's normal GC, and we avoid the
 *    background refetch that an explicit `removeQueries` on the live observer
 *    would kick off.
 */
export const useKioskCart = () => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const cartUuid = useLocalCartUuid();

  const v3 = useCart(
    { appMode: 'kiosk', uuid: cartUuid ?? '' },
    { enabled: !!useV3Cart && !!cartUuid }
  );
  const v1 = useGetCart(!useV3Cart);

  // A 404 means the cart was destroyed (e.g. last product removed). An
  // empty-but-existing cart isn't "gone", so we don't clear on size 0 — in v3
  // emptying the cart deletes it, so that case surfaces as the 404 anyway.
  const cartGone =
    !!useV3Cart &&
    !!cartUuid &&
    v3.error instanceof ApiError &&
    v3.error.response.status === 404;

  useEffect(() => {
    if (cartGone) {
      updateLocalCartUuid(null);
    }
  }, [cartGone]);

  return {
    // Returns the same `v3 | v1` union the call sites produced inline, so each
    // consumer's existing type handling (and v1/v3 bridging casts) is unchanged.
    // Falls back to EmptyCart rather than leaving `data` optional.
    data: (useV3Cart ? v3.data : v1.data) ?? EMPTY_CART,
    isFetching: useV3Cart ? v3.isFetching : v1.isFetching,
    isLoading: useV3Cart ? v3.isPending : v1.isPending,
    isSuccess: useV3Cart ? v3.isSuccess : v1.isSuccess,
  };
};
